import { useState, type ReactNode } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import {
  Button,
  IconButton,
  Input,
  Switch,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { useToast } from '@vnetwork/ui/feedback';
import { useCreateServiceNotification, type ServiceInformationPayload } from '@/api/services-mutations';

const DNS_TYPES = ['cname', 'a', 'aaaa', 'mx', 'txt', 'spf'];
const META_KEYS = ['id', 'ip', 'user', 'pass', 'link', 'config'];

function errMsg(err: unknown): string {
  const res = (err as { response?: { data?: { error_description?: string; error?: string; message?: string } } })?.response;
  return res?.data?.error_description ?? res?.data?.error ?? res?.data?.message ?? 'Something went wrong.';
}

type Meta = { key: string; value: string };
type Doc = { name: string; link: string };
type Dns = { name: string; type: string; target: string };

/**
 * Compose a technical notification (`POST /services/:id/technical`). Covers the
 * core `service_information` block: name, key/value metadata, documentation
 * links, and optional DNS records. File attachments are not handled here (they
 * need the upload flow) and are sent empty.
 */
export function ServiceNotificationDialog({
  serviceId,
  open,
  onOpenChange,
}: {
  serviceId: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const toast = useToast();
  const create = useCreateServiceNotification(serviceId);
  const [name, setName] = useState('');
  const [dnsEnable, setDnsEnable] = useState(false);
  const [meta, setMeta] = useState<Meta[]>([]);
  const [docs, setDocs] = useState<Doc[]>([]);
  const [dns, setDns] = useState<Dns[]>([]);

  if (!open) return null;

  const reset = () => {
    setName(''); setDnsEnable(false); setMeta([]); setDocs([]); setDns([]);
  };

  const submit = () => {
    const payload: ServiceInformationPayload = {
      notification_name: name.trim(),
      dns_enable: dnsEnable,
      dns_managements: dns.filter((d) => d.name || d.target),
      metadata: meta.filter((m) => m.key && m.value),
      documentations: docs.filter((d) => d.name || d.link),
    };
    create.mutate(payload, {
      onSuccess: () => { toast.success('Notification created'); reset(); onOpenChange(false); },
      onError: (e) => toast.error(errMsg(e)),
    });
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onOpenChange(false)}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>New notification</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
            <Field label="Name" required>
              {(p) => <Input {...p} value={name} onChange={(e) => setName(e.target.value)} placeholder="Notification title…" />}
            </Field>

            <RowGroup
              title="Metadata"
              rows={meta}
              onAdd={() => setMeta((r) => [...r, { key: META_KEYS[0], value: '' }])}
              onRemove={(i) => setMeta((r) => r.filter((_, x) => x !== i))}
              render={(row, i) => (
                <>
                  <Select value={row.key} onValueChange={(v) => setMeta((r) => r.map((x, xi) => (xi === i ? { ...x, key: v } : x)))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{META_KEYS.map((k) => <SelectItem key={k} value={k}>{k.toUpperCase()}</SelectItem>)}</SelectContent>
                  </Select>
                  <Input value={row.value} placeholder="Value" onChange={(e) => setMeta((r) => r.map((x, xi) => (xi === i ? { ...x, value: e.target.value } : x)))} />
                </>
              )}
            />

            <RowGroup
              title="Documentation"
              rows={docs}
              onAdd={() => setDocs((r) => [...r, { name: '', link: '' }])}
              onRemove={(i) => setDocs((r) => r.filter((_, x) => x !== i))}
              render={(row, i) => (
                <>
                  <Input value={row.name} placeholder="Name" onChange={(e) => setDocs((r) => r.map((x, xi) => (xi === i ? { ...x, name: e.target.value } : x)))} />
                  <Input value={row.link} placeholder="Link" onChange={(e) => setDocs((r) => r.map((x, xi) => (xi === i ? { ...x, link: e.target.value } : x)))} />
                </>
              )}
            />

            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
              <Switch checked={dnsEnable} onCheckedChange={setDnsEnable} />
              <span>DNS records</span>
            </div>
            {dnsEnable ? (
              <RowGroup
                title="DNS"
                rows={dns}
                onAdd={() => setDns((r) => [...r, { name: '', type: DNS_TYPES[0], target: '' }])}
                onRemove={(i) => setDns((r) => r.filter((_, x) => x !== i))}
                render={(row, i) => (
                  <>
                    <Input value={row.name} placeholder="Name" onChange={(e) => setDns((r) => r.map((x, xi) => (xi === i ? { ...x, name: e.target.value } : x)))} />
                    <Select value={row.type} onValueChange={(v) => setDns((r) => r.map((x, xi) => (xi === i ? { ...x, type: v } : x)))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{DNS_TYPES.map((k) => <SelectItem key={k} value={k}>{k.toUpperCase()}</SelectItem>)}</SelectContent>
                    </Select>
                    <Input value={row.target} placeholder="Target" onChange={(e) => setDns((r) => r.map((x, xi) => (xi === i ? { ...x, target: e.target.value } : x)))} />
                  </>
                )}
              />
            ) : null}
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={create.isPending}>Close</Button>
          <Button variant="primary" disabled={!name.trim() || create.isPending} onClick={submit}>Create</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RowGroup<T>({
  title,
  rows,
  onAdd,
  onRemove,
  render,
}: {
  title: string;
  rows: T[];
  onAdd: () => void;
  onRemove: (i: number) => void;
  render: (row: T, i: number) => ReactNode;
}) {
  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>{title}</span>
        <Button size="sm" variant="ghost" onClick={onAdd}><Plus size={14} /> Add</Button>
      </div>
      {rows.map((row, i) => (
        <div key={i} style={{ display: 'flex', gap: 'var(--vn-space-2)', alignItems: 'center' }}>
          {render(row, i)}
          <IconButton aria-label="Remove" variant="ghost" size="sm" onClick={() => onRemove(i)}><Trash2 size={15} /></IconButton>
        </div>
      ))}
    </div>
  );
}
